import React, { useEffect, useState } from "react";
import Modal from "./Modal";
import Button from "./Button";

/**
 * Replacement for window.prompt: a small form modal with validated fields.
 * fields: [{ name, label, type="text", defaultValue, placeholder, required, help, multiline, validate(value, all) -> string|undefined }]
 */
export default function PromptDialog({
  open,
  title = "Enter details",
  message,
  fields = [],
  confirmLabel = "Continue",
  cancelLabel = "Cancel",
  tone = "primary",
  onSubmit,
  onCancel,
}) {
  const [values, setValues] = useState({});
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (!open) return;
    const init = {};
    fields.forEach((f) => {
      init[f.name] = f.defaultValue == null ? "" : String(f.defaultValue);
    });
    setValues(init);
    setErrors({});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const submit = (e) => {
    if (e) e.preventDefault();
    const next = {};
    fields.forEach((f) => {
      const v = (values[f.name] || "").trim();
      if (f.required && !v) next[f.name] = `${f.label || f.name} is required`;
      else if (v && f.type === "number" && !(Number(v) >= 0)) next[f.name] = "Enter a valid number";
      else if (f.validate) {
        const msg = f.validate(v, values);
        if (msg) next[f.name] = msg;
      }
    });
    setErrors(next);
    if (Object.keys(next).length) return;
    const out = {};
    fields.forEach((f) => {
      out[f.name] = (values[f.name] || "").trim();
    });
    onSubmit && onSubmit(out);
  };

  return (
    <Modal
      open={open}
      onClose={onCancel}
      title={title}
      size="sm"
      tone={tone === "primary" ? undefined : tone}
      footer={
        <>
          <Button variant="secondary" onClick={onCancel}>
            {cancelLabel}
          </Button>
          <Button variant={tone === "danger" ? "danger" : "primary"} onClick={submit}>
            {confirmLabel}
          </Button>
        </>
      }
    >
      <form onSubmit={submit} noValidate>
        {message ? <p className="ee-confirm__msg">{message}</p> : null}
        {fields.map((f, i) => (
          <label className="ee-field" key={f.name}>
            <span className="ee-field__label">{f.label || f.name}</span>
            {f.multiline ? (
              <textarea
                className="form-control"
                rows={3}
                data-autofocus={i === 0 ? true : undefined}
                placeholder={f.placeholder}
                value={values[f.name] || ""}
                onChange={(e) => setValues({ ...values, [f.name]: e.target.value })}
              />
            ) : (
              <input
                className={`form-control${errors[f.name] ? " is-invalid" : ""}`}
                type={f.type === "number" ? "text" : f.type || "text"}
                inputMode={f.type === "number" ? "decimal" : undefined}
                data-autofocus={i === 0 ? true : undefined}
                placeholder={f.placeholder}
                value={values[f.name] || ""}
                onChange={(e) => setValues({ ...values, [f.name]: e.target.value })}
              />
            )}
            {f.help ? <span className="ee-field__help">{f.help}</span> : null}
            {errors[f.name] ? <span className="ee-field__error">{errors[f.name]}</span> : null}
          </label>
        ))}
        {/* lets Enter submit */}
        <button type="submit" className="eex-sr" tabIndex={-1}>
          Submit
        </button>
      </form>
    </Modal>
  );
}
